/**
 * Alarm chip list cell for server-backed grids.
 *
 * Renders a row's active alarms as AlarmChip pills. Rows can carry many
 * alarms, so only the first `limit` chips render inline; the rest collapse
 * into a "+N" overflow pill that opens a popover listing every alarm. The
 * popover click never reaches the row, so opening it cannot trigger row
 * navigation.
 */
import { Box, Popover, Tooltip } from '@mui/material';
import React, { useState } from 'react';

import AlarmChip from '@sophocles/components/AlarmChip';

const DEFAULT_INLINE_LIMIT = 3;

function ChipWithTooltip({ alarm }) {
  const chip = <AlarmChip label={alarm.label} color={alarm.color} />;
  return alarm.tooltip ? <Tooltip title={alarm.tooltip}>{chip}</Tooltip> : chip;
}

/**
 * @param {object} props
 * @param {Array<{ id: string|number, label: React.ReactNode,
 *   color?: string, tooltip?: React.ReactNode }>} props.alarms
 *   Alarms in display order; `color` is a status-palette family
 *   (see AlarmChip), `tooltip` an optional detail line.
 * @param {number} [props.limit] - Chips shown inline before the "+N" overflow.
 */
export default function AlarmChipCell({ alarms = [], limit = DEFAULT_INLINE_LIMIT }) {
  const [anchorEl, setAnchorEl] = useState(null);

  if (alarms.length === 0) return null;

  const inline = alarms.slice(0, limit);
  const overflowCount = alarms.length - inline.length;

  const openOverflow = event => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const closeOverflow = event => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-1)' }}>
      {inline.map(alarm => (
        <ChipWithTooltip key={alarm.id} alarm={alarm} />
      ))}
      {overflowCount > 0 && (
        <>
          <AlarmChip label={`+${overflowCount}`} color="neutral" sx={{ cursor: 'pointer' }} onClick={openOverflow} />
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={closeOverflow}
            onClick={event => event.stopPropagation()}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 'var(--space-1)',
                p: 'var(--space-2)',
                maxHeight: 320,
                overflowY: 'auto',
              }}
            >
              {alarms.map(alarm => (
                <ChipWithTooltip key={alarm.id} alarm={alarm} />
              ))}
            </Box>
          </Popover>
        </>
      )}
    </Box>
  );
}
